/* ═══════════════════════════════════════════════════════════════════════════
   Sloj tačaka na mapi — pumpe, klijenti, carine, parkinzi i sedišta firmi.

   Pinovi se razlikuju po TIPU, i to bojom i ikonom: na mapi punoj vozila se
   „još jedan plavi marker" ne čita. Tip je šifarnik (model_gen), pa se izgled
   vezuje za naziv tipa — ne za id, koji je u svakoj bazi drugi.

   Sedišta firmi dolaze iz `company.Firma`, ne iz tabele tačaka, pa imaju svoj
   izgled i svoj prekidač.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useMemo } from 'react';
import { Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';

/* Izgled po tipu. Ključ je naziv tipa iz šifarnika, sveden na mala slova bez
   dijakritike — tip se u bazi zove „Granični prelaz", a ovde ga treba pogoditi
   bez obzira na to kako je otkucan. Nepoznat tip dobija neutralan pin, da nova
   stavka u šifarniku ne nestane sa mape. */
const IZGLED = {
    pumpa:            { ikona: 'pi-bolt',          boja: '#e8590c' },
    klijent:          { ikona: 'pi-briefcase',     boja: '#1b6ec2' },
    carina:           { ikona: 'pi-shield',        boja: '#6741d9' },
    parking:          { ikona: 'pi-stop-circle',   boja: '#2b8a3e' },
    servis:           { ikona: 'pi-wrench',        boja: '#c92a2a' },
    magacin:          { ikona: 'pi-box',           boja: '#495057' },
    'granicni prelaz': { ikona: 'pi-flag',         boja: '#862e9c' },
    ostalo:           { ikona: 'pi-map-marker',    boja: '#868e96' },
};
const IZGLED_SEDISTE = { ikona: 'pi-home', boja: '#0b7285' };
const IZGLED_PODRAZUMEVANI = IZGLED.ostalo;

export const kljucTipa = (naziv) => String(naziv || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[ćč]/gi, 'c').replace(/[šś]/gi, 's').replace(/[žź]/gi, 'z').replace(/đ/gi, 'dj')
    .toLowerCase().trim();

export const izgledTipa = (nazivTipa) => IZGLED[kljucTipa(nazivTipa)] || IZGLED_PODRAZUMEVANI;

/* Ikone se prave JEDNOM po izgledu i keširaju. Nova `L.divIcon` na svaki render
   je ono što je ranije obaralo mapu sa hiljadu markera (vidi OP-81). */
const kesIkona = new Map();

const napraviIkonu = ({ ikona, boja }, sediste = false) => {
    const kljuc = `${ikona}|${boja}|${sediste ? 1 : 0}`;
    if (kesIkona.has(kljuc)) return kesIkona.get(kljuc);

    const velicina = sediste ? 34 : 28;
    const html = `
        <div class="gps-tacka-pin${sediste ? ' gps-tacka-sediste' : ''}" style="--pin: ${boja}">
            <i class="pi ${ikona}"></i>
        </div>`;

    const icon = L.divIcon({
        html,
        className: 'gps-tacka-ikona',
        iconSize: [velicina, velicina],
        /* Vrh „kapljice" je na dnu elementa — sidro mora tu, inače pin pokazuje
           pored mesta na koje je postavljen. */
        iconAnchor: [velicina / 2, velicina],
        popupAnchor: [0, -velicina],
    });
    kesIkona.set(kljuc, icon);
    return icon;
};

const Red = ({ naslov, vrednost }) => (
    vrednost ? <div className="gps-tacka-red"><span>{naslov}</span>{vrednost}</div> : null
);

/* `vidljiviTipovi` je skup ključeva tipova koji se prikazuju; `null` = svi. */
const SlojTacaka = ({ tacke = [], sedista = [], vidljiviTipovi = null, prikaziSedista = true, t }) => {
    const zaPrikaz = useMemo(() => tacke.filter(x =>
        !vidljiviTipovi || vidljiviTipovi.has(kljucTipa(x.tipOpis))), [tacke, vidljiviTipovi]);

    return (
        <>
            {zaPrikaz.map(x => (
                <Marker key={`t${x.id}`} position={[Number(x.lat), Number(x.lon)]}
                        icon={napraviIkonu(izgledTipa(x.tipOpis))}>
                    <Tooltip direction="top" offset={[0, -26]}>{x.Naziv}</Tooltip>
                    <Popup>
                        <div className="gps-tacka-popup">
                            <strong>{x.Naziv}</strong>
                            <div className="gps-tacka-tip">{x.tipOpis}</div>
                            <Red naslov={t('admin.adresa')} vrednost={[x.Adresa, x.Mesto].filter(Boolean).join(', ')} />
                            <Red naslov={t('nova.napomena')} vrednost={x.Napomena} />
                            <a href={`https://www.google.com/maps?q=${x.lat},${x.lon}`}
                               target="_blank" rel="noreferrer">{t('pracenje.otvoriKartu')}</a>
                        </div>
                    </Popup>
                </Marker>
            ))}

            {prikaziSedista && sedista.map(f => (
                <Marker key={`s${f.firma_id}`} position={[Number(f.lat), Number(f.lon)]}
                        icon={napraviIkonu(IZGLED_SEDISTE, true)}>
                    <Tooltip direction="top" offset={[0, -32]}>{f.Naziv}</Tooltip>
                    <Popup>
                        <div className="gps-tacka-popup">
                            <strong>{f.Naziv}</strong>
                            <div className="gps-tacka-tip">{t('tacke.sediste')}</div>
                            <Red naslov={t('admin.adresa')} vrednost={[f.Adresa, f.Mesto].filter(Boolean).join(', ')} />
                            <a href={`https://www.google.com/maps?q=${f.lat},${f.lon}`}
                               target="_blank" rel="noreferrer">{t('pracenje.otvoriKartu')}</a>
                        </div>
                    </Popup>
                </Marker>
            ))}
        </>
    );
};

export default SlojTacaka;
